import React, { useMemo } from 'react'
import styled from 'styled-components'
import { WidgetId } from 'features/types'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useDashboardWidgetData } from 'hooks/useWidgetState'
import { AnnotatedMetricResult } from 'schemas/AnnotatedMetricResultSchema'
import { MeasureTypeStrings } from 'enums/MeasureType'
import Tile, { TileProps } from 'components/visualization/Tile'
import { DashboardWidget } from 'features/widget/types'
import { SummaryMetricsCompactWidgetsType } from '../../types'
import { retrieveDataset } from '../utils'
import convertToTileData from './convertToTileData'
import getSortAndFilterByMeasuresFunction from './getSortAndFilterByMeasuresFunction'

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(204px, 1fr));
  gap: 8px;
  height: 100%;
`

type DashboardWidgetWithoutFailureMessage<T> = Omit<DashboardWidget<T>, 'failureMessage'>

interface SummaryMetricsCompactProps {
  className?: string
  widgetSetting: SummaryMetricsCompactWidgetsType
  widgetData: DashboardWidgetWithoutFailureMessage<AnnotatedMetricResult>
  customTile?: React.ComponentType<TileProps>
}

export const SummaryMetricsCompact = ({
  className,
  widgetSetting,
  widgetData,
  customTile
}: SummaryMetricsCompactProps) => {
  const { showChange, measures } = widgetSetting

  /*
   * The dateset in the widgetData.data does not gurantee the order nor the visibility of tiles.
   * Therefore, widgetSetting.measures is used here to sort and filter the dataset before conversion.
   */
  const sortAndFilterByMeasures = useMemo(
    () => getSortAndFilterByMeasuresFunction(measures as MeasureTypeStrings[]),
    [measures]
  )
  const [dataset, prevDataset] = useMemo(
    () => [
      sortAndFilterByMeasures(retrieveDataset(widgetData.data[0])),
      sortAndFilterByMeasures(retrieveDataset(widgetData.data[1]))
    ],
    [sortAndFilterByMeasures, widgetData.data]
  )

  const tileData = useMemo(() => convertToTileData(dataset, prevDataset), [dataset, prevDataset])
  const TileComponent = customTile || Tile

  return (
    <Container className={className}>
      {tileData.map(({ title, value, colorSetting, variation, isDisabled, infoTooltipContent }) => (
        <TileComponent
          key={title}
          title={title}
          value={value}
          colorSetting={colorSetting}
          variation={showChange ? variation : undefined}
          size={widgetSetting.sizeY === 1 ? 'sm' : 'md'}
          isDisabled={isDisabled}
          infoTooltipContent={infoTooltipContent}
        />
      ))}
    </Container>
  )
}

const SummaryMetricsCompactWidget = ({ widgetId }: { widgetId: WidgetId }) => {
  const widgetSetting = useDashboardWidgetSetting<SummaryMetricsCompactWidgetsType>(widgetId)
  const widgetData = useDashboardWidgetData<AnnotatedMetricResult>(widgetId)

  return <SummaryMetricsCompact widgetSetting={widgetSetting} widgetData={widgetData} />
}

export default SummaryMetricsCompactWidget
